import * as z from 'zod';
import { isoDateTimeSchema, strict, uuidSchema } from './common.js';
/**
 * AD-38: the archive is a directory, and nothing is encrypted twice.
 *
 * `manifest.json` is cleartext, because it has to be readable before anything is decrypted, and it
 * therefore contains no personal data at all: versions, counts, and a digest per item. **Every
 * digest is computed over the bytes AS STORED**, which for an object means the ciphertext. A digest
 * over plaintext would ship a confirmation oracle in every retained archive: anyone holding a
 * candidate photograph could test it against a record, including a record that had since been
 * cryptographically erased.
 *
 * `schemaVersion` is what makes a restore refuse an archive it cannot understand: the binary
 * declares the highest version it knows, and a newer archive is refused rather than half-applied.
 */
export const backupKindSchema = z.enum(['manual_full', 'scheduled_db']);
/**
 * Who asked for an archive, which is a different question from what the archive CONTAINS.
 *
 * **The two were conflated and the panel's button silently did nothing because of it.** There are
 * only two kinds and only one of them is database-only, so `routes/backup/index.ts` maps an
 * administrator's "take a database archive" onto `scheduled_db` — the same kind the nightly cron
 * fires with. The handler then read that kind as evidence of who asked and skipped the run whenever
 * `backup.schedule_enabled` was off, which is the shipped default: the route answered
 * `202 accepted`, no `backups` row was ever written, and the list stayed empty. The same conflation
 * decided the mail, so a manual database archive was posted to the nightly recipient list.
 *
 * It never travels on the wire — a caller cannot claim to be the scheduler — and lives here rather
 * than in the worker because it is the backup domain's vocabulary and the kind it qualifies is
 * here. The job payload defaults it to `scheduled`, so the cron, which sends no payload at all,
 * keeps exactly the behaviour it had.
 */
export const backupTriggerSchema = z.enum(['scheduled', 'manual']);
export const backupStatusSchema = z.enum(['running', 'ok', 'failed']);
/** What a delivery attempt did, or that none was made. Mirrors the `backup_email_status` enum. */
export const backupEmailStatusSchema = z.enum(['not_attempted', 'sent', 'too_large', 'failed']);
/** A SHA-256 digest as lower-case hexadecimal, over the bytes as stored. */
export const digestSchema = z.string().regex(/^[0-9a-f]{64}$/u, {
    error: 'errors.validation.invalid_format',
});
/**
 * A path INSIDE an archive: one or more slash-separated segments of the safe alphabet.
 *
 * **A manifest arrives from a file, so it is untrusted input, and a restore joins this onto a
 * scratch directory and reads it.** Left as a free string, `objects/../../../etc/passwd` satisfies
 * every other check in the manifest — it digests correctly, because the file really is there — so a
 * malformed archive is read off the host and refused only at `assertObjectKey`, which happens at
 * step 6, after `pg_restore` has already replaced the database. Nothing is disclosed either way,
 * but the refusal belongs before the point of no return rather than after it.
 *
 * The grammar admits exactly what an archive contains: `dump.pgc.enc`, and `objects/<uuid4>`. No
 * leading slash, no `.` or `..` segment, no backslash, no empty segment, and a total length that
 * cannot exhaust a path buffer.
 */
const SAFE_ARCHIVE_SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]*$/u;
export const manifestPathSchema = z
    .string()
    .min(1)
    .max(512)
    // Checked segment by segment rather than by one pattern over the whole path: the natural
    // `^seg(?:/seg)*$` nests a quantifier inside a quantifier, which is the shape that backtracks
    // exponentially on a long near-match, and a manifest is attacker-supplied by construction.
    .refine((value) => value.split('/').every((segment) => segment !== '..' && SAFE_ARCHIVE_SEGMENT.test(segment)), { error: 'errors.validation.invalid_format' });
/** One entry of the manifest. A path and a size and a digest: nothing that describes a person. */
export const manifestItemSchema = strict({
    path: manifestPathSchema,
    bytes: z.int().nonnegative(),
    digest: digestSchema,
});
/**
 * The AD-50 `aad` string, recorded in the manifest because it is part of the archive FORMAT.
 *
 * The encryption library concatenates its `aad` option into the header binding of everything its
 * file and text paths produce, so a binary whose `CRYPTO_MANAGER_OPTIONS.aad` differs from the one
 * that wrote an archive cannot open that archive at all. Recording it is what turns that from a
 * generic decryption failure discovered on the first real restore into a named refusal discovered
 * before a single byte is written. Bounded and constrained to a printable, delimiter-free token so
 * that a manifest cannot smuggle anything else through this field.
 */
export const cryptoAadSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[\w/.-]+$/u, {
    error: 'errors.validation.invalid_format',
});
export const backupManifestSchema = strict({
    formatVersion: z.int().positive(),
    appVersion: z.string().max(32),
    schemaVersion: z.int().positive(),
    keyVersion: z.int().nonnegative(),
    /** AD-50: part of the on-disk format, not a tuning knob. See {@link cryptoAadSchema}. */
    cryptoAad: cryptoAadSchema,
    createdAt: isoDateTimeSchema,
    kind: backupKindSchema,
    /**
     * One entry per table in the archive, keyed by its SQL name.
     *
     * The key set is the archive's table list, which is what a restore compares against the set the
     * binary expects before `pg_restore` begins: an archive taken during a broken-privileges window
     * is missing a table, and a dump that is silently short of a table is the worst failure this
     * product can have. A plaintext-marker grep over the archive cannot detect an ABSENT table, so
     * the list is recorded rather than inferred.
     */
    counts: z.record(z.string().max(64), z.int().nonnegative()),
    /**
     * How many stored objects travelled with this archive.
     *
     * Zero for a `scheduled_db` archive, which carries no `objects/` at all — and that zero is load
     * bearing on the restore side, because it is what tells a restore not to touch the bucket rather
     * than to empty it (Section 1.14, item 12).
     */
    objectCount: z.int().nonnegative(),
    items: z.array(manifestItemSchema),
});
export const backupSchema = strict({
    id: uuidSchema,
    kind: backupKindSchema,
    status: backupStatusSchema,
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    bytes: z.int().nonnegative().nullable(),
    digest: digestSchema.nullable(),
    emailStatus: backupEmailStatusSchema.nullable(),
    createdBy: uuidSchema.nullable(),
    /**
     * Whether this archive's file is on the volume RIGHT NOW, which is not the same as `status`.
     *
     * **A successful archive can stop being downloadable, and the panel has no other way to know.**
     * Retention sweeps the file and sets `file_path` to null while the row stays `ok` — which
     * `retention.ts` calls the honest statement that the archive existed and is gone — and a restore
     * after a total loss brings back rows describing a volume that no longer exists. Without this
     * member a client can only read `status`, so it offers a download for every successful archive
     * and some of them 404: the button is there, the click does nothing, and nothing says why. The
     * path itself is deliberately NOT exposed — a filesystem layout is not a client's business — so
     * what travels is the one boolean a client can act on.
     */
    downloadable: z.boolean(),
});
/** Starting an archive by hand. The scheduled one takes its shape from the settings registry. */
export const createBackupRequestSchema = strict({
    /** A full archive includes `objects/`; the nightly one is the database alone. */
    includeObjects: z.boolean().default(true),
});
/**
 * Restoring.
 *
 * The typed confirmation and the maintenance flag are both required, because a restore replaces
 * every row in the installation and there is no undo beyond the snapshot the restore itself takes.
 */
export const restoreRequestSchema = strict({
    backupId: uuidSchema.optional(),
    uploadedPath: z.string().min(1).max(512).optional(),
    confirmation: z.literal('RESTORE'),
    acknowledgeDataLoss: z.literal(true),
}).refine((request) => (request.backupId === undefined) !== (request.uploadedPath === undefined), {
    error: 'errors.validation.invalid_option',
    path: ['backupId'],
});
/** What a restore reports before it does anything, so the operator sees what they are about to lose. */
export const restorePreviewSchema = strict({
    manifest: backupManifestSchema,
    schemaVersionSupported: z.boolean(),
    currentCounts: z.record(z.string().max(64), z.int().nonnegative()),
});
/**
 * What ARMING a restore answers (AD-49).
 *
 * The panel cannot execute a restore, because the drain stops the very process serving the request.
 * So it verifies the archive, shows what is in it, takes the typed confirmation, writes maintenance
 * mode plus the pending marker, and hands back the exact command. `command` is a literal an operator
 * copies, which is why it is in the response rather than in the documentation: a runbook and a
 * product drift, and the one that drifts is the runbook.
 */
export const restoreArmedSchema = strict({
    preview: restorePreviewSchema,
    /** `docker compose run --rm -it cli sentra restore --from <path>`, fully formed. */
    command: z.string().min(1).max(512),
    /** Where the archive the operator just verified is, so the command and the file cannot diverge. */
    archivePath: z.string().min(1).max(512),
});
/**
 * What the status endpoint answers, and what AD-49's polling loop reads.
 *
 * It answers at all only once the api is back, which is the whole mechanism: an admin panel polling
 * this sees a connection error, then a 502 from the inner Nginx, then this — and `pendingRestore`
 * going false is the api reporting that it has cold-started against the restored key material.
 */
export const maintenanceStatusSchema = strict({
    active: z.boolean(),
    /** A closed token, never prose: `restore-pending`, or whatever an administrator declared. */
    reason: z.string().max(64).nullable(),
    startedAt: isoDateTimeSchema.nullable(),
    /** True while the marker a restore left is still set, which means the api has not cold-started. */
    pendingRestore: z.boolean(),
});
/**
 * Turning maintenance mode on or off by hand.
 *
 * The escape hatch, and the reason it needs one: a restore leaves the marker set for a cold start
 * that, on a stack somebody has stopped, may never come. Turning it ON is deliberately offered too,
 * because planned maintenance is a real thing an administrator does and a flag only a restore could
 * set would be one they reached for SQL to get.
 */
export const setMaintenanceRequestSchema = strict({
    active: z.boolean(),
    /**
     * A lower-case token, and never the one a restore reserves.
     *
     * `restore-pending` is the marker the api clears at boot as its proof of a cold start, so an
     * administrator who typed it by hand would have their planned maintenance window silently ended
     * by the next restart — and would have no way to tell that from the window simply not working.
     * Refused here rather than tolerated, because the value is compared rather than read.
     */
    reason: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][\w-]*$/u, { error: 'errors.validation.invalid_format' })
        .refine((value) => value !== 'restore-pending', {
        error: 'errors.validation.invalid_option',
    })
        .optional(),
});
/**
 * Destroying every retained archive, now.
 *
 * Section 1.14 item 2 states the risk this answers: an erasure's latency is the archive retention
 * window, because a shredded person's wrapped key still exists inside every archive taken before
 * the shred. There is no way to remove one person from an archive — the dump is encrypted whole —
 * so the only honest control is to destroy the archives, and it is offered for exactly that case.
 *
 * **The typed confirmation is in the REQUEST rather than only in the interface**, exactly as a
 * restore's is: this is the one operation that removes the installation's own recovery path, and a
 * client that reached it without saying the word would be a client that reached it by accident.
 */
export const purgeArchivesRequestSchema = strict({
    confirmation: z.literal('PURGE'),
    acknowledgeNoRecovery: z.literal(true),
});
/**
 * What a purge destroyed.
 *
 * `kept` is zero by construction — a purge keeps nothing — and it is reported anyway, because the
 * same sweep runs nightly with a retention count and a report that changed shape between the two
 * would be two reports. `failed` names archives whose file could not be removed, by ID rather than
 * by path: a path names the volume's layout.
 */
export const purgeArchivesReportSchema = strict({
    deleted: z.int().nonnegative(),
    kept: z.int().nonnegative(),
    failed: z.array(uuidSchema),
});
/** The presence reconciliation offered after a restore, because the building moved on. */
export const reconciliationRequestSchema = strict({
    corrections: z
        .array(strict({ personId: uuidSchema, setState: z.enum(['inside', 'outside']) }))
        .max(10_000),
});
//# sourceMappingURL=backup.js.map